import * as api from 'api/endpoints/mainContacts/mainContacts.api';
import * as MainContactsTypes from './mainContacts.types';
import { mainContactError } from 'utils/errorCodes';
import { notificationErr } from 'views/components/UI/notification';
import { messageError, messageSuccess } from 'views/components/UI/message';
import { ITranslate } from 'interfaces/general.interface';
import { Dispatch } from 'react';
import {
  IcreateMainContactAPIResponseBody,
  IgetMainContactsAPIResponseItemContact,
  IupdateMainContactAPIBody,
} from 'api/endpoints/mainContacts/mainContacts.response.interface';
import { TmainContactsDispatchReducer } from './mainContacts.reducer';
import { addNotificationsTypeToContactAPI } from 'api/endpoints/notifications/notifications.api';

export async function getMainContactsAction({
  dispatch,
  tenantId,
  token,
  translate,
}: {
  dispatch: Dispatch<TmainContactsDispatchReducer>;
  tenantId: string;
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  dispatch({ type: MainContactsTypes.SET_IS_LOADING_CONTACTS, payload: true });
  try {
    const contactsFetch = await api.getMainContactsAPI(tenantId, token);
    if (contactsFetch.response.status === 200) {
      dispatch({
        type: MainContactsTypes.GET_MAIN_CONTACTS,
        payload: contactsFetch.data.company_tenant_contacts,
      });
      return true;
    }
    if (contactsFetch.response.status === 404) {
      return false;
    }
    messageError(translate('actions_main-contacts_get-main-contacts_error'));
    notificationErr(mainContactError.get_contacts_002, translate);
    return false;
  } catch (err) {
    messageError(translate('actions_main-contacts_get-main-contacts_error'));
    notificationErr(mainContactError.get_contacts_001, translate);
    return false;
  } finally {
    dispatch({ type: MainContactsTypes.SET_IS_LOADING_CONTACTS, payload: false });
  }
}

export function selectContactAction({
  dispatch,
  contact,
}: {
  dispatch: Dispatch<TmainContactsDispatchReducer>;
  contact: IgetMainContactsAPIResponseItemContact | null;
}) {
  return dispatch({
    type: MainContactsTypes.SELECT_CONTACT,
    payload: contact,
  });
}

export async function createContactAction({
  dispatch,
  tenantId,
  contact,
  token,
  translate,
}: {
  dispatch: Dispatch<TmainContactsDispatchReducer>;
  tenantId: string;
  contact: IcreateMainContactAPIResponseBody & { notifications?: Array<string> };
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  delete contact.notifications;
  try {
    const createFetch = await api.createMainContactAPI({
      tenantId,
      contact,
      token,
    });
    if (createFetch.response.status === 200 || createFetch.response.status === 201) {
      dispatch({
        type: MainContactsTypes.CREATE_CONTACT,
        payload: createFetch.data,
      });
      messageSuccess(translate('actions_main-contacts_create-main-contact_success'));
      return true;
    } else {
      messageError(translate('actions_main-contacts_create-main-contact_error'));
      notificationErr(mainContactError.create_contact_002, translate);
      return false;
    }
  } catch (err) {
    messageError(translate('actions_main-contacts_create-main-contact_error'));
    notificationErr(mainContactError.create_contact_001, translate);
    return false;
  }
}

export async function updateContactAction({
  dispatch,
  contactSelectedId,
  contactEntityId,
  contact,
  token,
  translate,
}: {
  dispatch: Dispatch<TmainContactsDispatchReducer>;
  contactSelectedId: string;
  contactEntityId: string;
  contact: IcreateMainContactAPIResponseBody & { notifications?: Array<string> };
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  try {
    const notifications: string[] = contact.notifications ?? [];
    delete contact.notifications;
    const contactId = contactSelectedId;
    const body: IupdateMainContactAPIBody = {
      filter: { id: contactSelectedId },
      update: contact,
    };
    const updateFetch = await api.updateMainContactAPI({ body, token });
    if (updateFetch.response.status === 200 && updateFetch.data[0] === 1) {
      try {
        const addNotificationsTypes = await addNotificationsTypeToContactAPI({
          contactId: contactEntityId,
          notificationsTypesIds: notifications,
          token,
        });
        if (
          addNotificationsTypes.response.status === 200 ||
          addNotificationsTypes.response.status === 201
        ) {
          dispatch({
            type: MainContactsTypes.UPDATE_CONTACT,
            payload: {
              id: contactId,
              contact,
              notifications: Array.isArray(addNotificationsTypes.data)
                ? addNotificationsTypes.data
                : [],
            },
          });
          messageSuccess(translate('actions_main-contacts_update-main-contact_success'));
          return true;
        }
        messageError('Error al actualizar los tipos de notificaciones.');
        notificationErr(mainContactError.update_contact_004, translate);
        return false;
      } catch (error) {
        messageError('Error al actualizar el usuario.');
        notificationErr(mainContactError.update_contact_003, translate);
        return false;
      }
    } else {
      messageError(translate('actions_main-contacts_update-main-contact_error'));
      notificationErr(mainContactError.update_contact_001, translate);
      return false;
    }
  } catch (err) {
    messageError(translate('actions_main-contacts_update-main-contact_error'));
    notificationErr(mainContactError.update_contact_002, translate);
    return false;
  }
}

export async function removeContactAction({
  dispatch,
  contactId,
  token,
  translate,
}: {
  dispatch: Dispatch<TmainContactsDispatchReducer>;
  contactId: string;
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  try {
    const removeFetch = await api.removeMainContactAPI({ contactId, token });
    if (removeFetch.response.status === 200 && removeFetch.data.contact === 1) {
      dispatch({
        type: MainContactsTypes.REMOVE_CONTACT,
        payload: contactId,
      });
      messageSuccess(translate('actions_main-contacts_delete-main-contact_success'));
      return true;
    } else {
      messageError(translate('actions_main-contacts_delete-main-contact_error'));
      notificationErr(mainContactError.remove_contact_001, translate);
      return false;
    }
  } catch (err) {
    messageError(translate('actions_main-contacts_delete-main-contact_error'));
    notificationErr(mainContactError.remove_contact_002, translate);
    return false;
  }
}
